<template>
  <el-button
    v-if="showHandler"
    type="primary"
    :class="[bem.b(), 'ep-button']"
    size="small"
    @click="open"
    >{{ t('ep.customColumn.columnSettings') }}</el-button
  >
  <CustomColumnDialog>
    <el-transfer
      v-model="showData"
      :class="bem.e('transfer')"
      filterable
      :titles="[
        t('ep.customColumn.hiddenField'),
        t('ep.customColumn.visibleField'),
      ]"
      :button-texts="[
        t('ep.customColumn.moveLeft'),
        t('ep.customColumn.moveRight'),
      ]"
      :data="allData"
      target-order="push"
      @right-check-change="rightCheckChange"
      @change="change"
    >
      <template #left-footer>
        <div :class="bem.e('operate')">
          <el-button
            type="primary"
            class="up"
            :disabled="isDiabledMoveUpDown"
            @click="moveUp"
          >
            <el-icon><arrow-up /></el-icon>
            {{ t('ep.customColumn.moveUp') }}
          </el-button>
          <el-button
            type="primary"
            class="down"
            :disabled="isDiabledMoveUpDown"
            @click="moveDown"
          >
            {{ t('ep.customColumn.moveDown') }}
            <el-icon><arrow-down /></el-icon>
          </el-button>
        </div>
      </template>
    </el-transfer>
  </CustomColumnDialog>
</template>

<script setup lang="tsx">
import { ref, computed, watch, provide } from 'vue'
import { useDialog } from '@el-plus/hooks/dialog/use-dialog'
import { createNameSpace } from '@el-plus/utils/bem'
import { useLocale } from '@el-plus/hooks/use-locale'
import { ArrowUp, ArrowDown } from '@element-plus/icons-vue'
import { customColumnEmits, customColumnProps } from './custom-column'
import { useRequest } from '@el-plus/hooks/use-request'
import type { TransferDataItem } from 'element-plus'
import { bus } from '@el-plus/utils'

const { t } = useLocale()
const bem = createNameSpace('custom-column')
defineOptions({ name: 'EpCustomColumn', inheritAttrs: false })
const props = defineProps(customColumnProps)
const emit = defineEmits(customColumnEmits)
const CustomColumnDialog = useDialog({
  class: bem.e('dialog'),
  width: 850,
  center: true,
  title: t('ep.customColumn.columnSettings'),
  onConfirm: async (resolve) => {
    resolve(
      saveColumnsFn!({
        module: props.module,
        userModuleColumnsReqs: showData.value.map((item, index) => {
          return {
            columnId: item,
            seq: index,
          }
        }),
      }).then(() => {
        const columns = showTableColumns()
        emit('confirm', columns)
        bus.emit('loadedCustomColumn', columns)
      }),
    )
  },
})
const allData = ref<TransferDataItem[]>([])
const showData = ref<TransferDataItem[]>([])
if (typeof props.api === 'function') {
  console.warn(
    `请按照默认自定义列的格式返回数据，
    格式为{
        hiddenColumns: [{id: '', showName: '', showField: ''}], 
        showColumns: [{id: '', showName: '', showField: ''}]
      }
    `,
  )
}
const { requestFn: getColumnsFn } = useRequest({
  api: props.api,
  method: 'post',
  hooks: {
    success(data) {
      const { hiddenColumns, showColumns } = data as {
        hiddenColumns: TransferDataItem[]
        showColumns: TransferDataItem[]
      }
      allData.value = [...hiddenColumns, ...showColumns].map((item) => ({
        label: item.showName,
        key: item.id,
        prop: item.showField,
      }))
      showData.value = showColumns.map((item) => item.id)
      const columns = showTableColumns()
      emit('loaded', columns)
      bus.emit('loadedCustomColumn', columns)
    },
  },
})
if (typeof props.saveApi === 'function') {
  console.warn(
    `请按照默认自定义列的格式提交数据，
    格式为{
          module: xx,
          userModuleColumnsReqs: [{columnId: xx, seq: 序号index}]
      }
    `,
  )
}
const { requestFn: saveColumnsFn } = useRequest({
  api: props.saveApi,
  method: 'post',
  hooks: {
    success() {
      emit('confirm', showTableColumns())
    },
  },
})
const showTableColumns = () => {
  return showData.value.map((id) => {
    const item = allData.value.find((item) => item.key === id)
    return {
      prop: item!.prop,
      label: item!.label,
      key: item!.key,
    }
  })
}
if (props.module) {
  getColumnsFn!({
    module: props.module,
  })
}
const rightChecked = ref([])

const moveUp = () => {
  const index = showData.value.indexOf(rightChecked.value[0])
  const temp = showData.value[index]
  if (index === 0) {
    showData.value[index] = showData.value[showData.value.length - 1]
    showData.value[showData.value.length - 1] = temp
  } else {
    showData.value[index] = showData.value[index - 1]
    showData.value[index - 1] = temp
  }
  scrollIntoView()
}
const moveDown = () => {
  const index = showData.value.indexOf(rightChecked.value[0])
  const temp = showData.value[index]
  if (index === showData.value.length - 1) {
    showData.value[index] = showData.value[0]
    showData.value[0] = temp
  } else {
    showData.value[index] = showData.value[index + 1]
    showData.value[index + 1] = temp
  }
  scrollIntoView()
}
const scrollIntoView = () => {
  // 选中项要在可视范围
  const checkedEl = document.querySelectorAll(
    '.ep-custom-column__transfer .el-transfer-panel',
  )
  const rightEl = checkedEl[1].querySelector(
    '.el-transfer-panel__item.is-checked',
  )
  if (rightEl) {
    requestAnimationFrame(() => {
      rightEl.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })
    })
  }
}

const rightCheckChange = (val) => {
  rightChecked.value = val
}
const change = () => {
  if (!showData.value.some((item) => item === rightChecked.value[0])) {
    rightChecked.value = []
  }
}

const isDiabledMoveUpDown = computed(() => {
  return rightChecked.value.length !== 1
})

const open = async () => {
  try {
    await CustomColumnDialog.open()
  } catch (error) {}
}
defineExpose({ open })
</script>
